import { useEffect, useState, type ReactNode } from "react";
import type {
  Actor,
  Command,
  GuildRecord,
  JobView,
} from "@guildmaster/core/platform";
import { api, ApiError, type PortalConfig } from "./api";

export interface AskContext {
  nonce: string;
  prompt: string;
  characterId?: string;
  reportIds?: string[];
}
type Mode = "explain" | "player" | "guild";
export function MemberAsk({
  actor,
  context,
  config,
  jobs,
  initial,
  onSubmit,
  onDenied,
  renderResult,
  onContextUsed,
  onChange,
}: {
  actor: Actor;
  context: GuildRecord["contextId"];
  config: PortalConfig | null;
  jobs: JobView[];
  initial?: AskContext;
  onContextUsed: () => void;
  onChange: (id: string, action: "cancel" | "resume") => Promise<void>;
  onSubmit: (command: Command) => Promise<boolean>;
  onDenied: (err: unknown) => void;
  renderResult: (value: unknown) => ReactNode;
}) {
  const key = `gm:conversation:${actor.userId}:${context}`;
  const restore = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(key) ?? "null") as {
        id?: string;
        mode?: Mode;
        characterId?: string;
        reportIds?: string[];
      } | null;
      return value?.id &&
        /^[a-zA-Z0-9_.:-]{1,100}$/.test(value.id) &&
        ["explain", "player", "guild"].includes(value.mode ?? "")
        ? {
            id: value.id,
            mode: value.mode!,
            characterId: value.characterId,
            reportIds: value.reportIds,
          }
        : { id: crypto.randomUUID(), mode: "explain" as Mode };
    } catch {
      return { id: crypto.randomUUID(), mode: "explain" as Mode };
    }
  };
  const [thread, setThread] = useState(restore),
    [prompt, setPrompt] = useState(""),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState<JobView[]>([]),
    [error, setError] = useState("");
  useEffect(() => {
    if (initial) {
      setThread({
        id: crypto.randomUUID(),
        mode: "explain",
        characterId: initial.characterId,
        reportIds: initial.reportIds,
      });
      setPrompt(initial.prompt);
      onContextUsed();
    }
  }, [initial?.nonce]);
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(thread));
    } catch {
      /* Optional resume metadata only; no answers are stored in the browser. */
    }
  }, [key, thread]);
  const stamp = jobs.map((job) => job.id + job.state).join(":");
  useEffect(() => {
    let active = true;
    void api<JobView[]>(
      "/api/conversation?" +
        new URLSearchParams({ context, id: thread.id, mode: thread.mode }),
    )
      .then((value) => {
        if (active) {
          setSaved(value);
          setError("");
        }
      })
      .catch((err) => {
        if (!active) return;
        if (err instanceof ApiError && [401, 403].includes(err.status)) {
          setSaved([]);
          onDenied(err);
        }
        setError(
          err instanceof Error ? err.message : "Conversation unavailable",
        );
      });
    return () => {
      active = false;
    };
  }, [context, thread.id, thread.mode, stamp, onDenied]);
  const relevant = jobs.filter(
    (job) =>
      job.command.contextId === context && job.command.type === "analysis",
  );
  const turns = Array.from(
    new Map(
      [
        ...saved,
        ...relevant.filter(
          (job) =>
            job.command.type === "analysis" &&
            job.command.conversationId === thread.id &&
            job.command.mode === thread.mode,
        ),
      ].map((job) => [job.id, job]),
    ).values(),
  ).sort((a, b) => a.createdAt - b.createdAt);
  const threads = Array.from(
    new Map(
      relevant.map((job) =>
        job.command.type === "analysis"
          ? ([job.command.conversationId, job] as const)
          : ([job.id, job] as const),
      ),
    ).values(),
  );
  const newThread = (mode: Mode = "explain") => {
    setSaved([]);
    setThread({ id: crypto.randomUUID(), mode });
    setPrompt("");
  };
  const active = turns.some((job) =>
    ["queued", "running", "paused"].includes(job.state),
  );
  return (
    <div className="member-portal">
      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">PRIVATE CONVERSATION</p>
            <h2>Ask Guildmaster</h2>
          </div>
          <button className="button" onClick={() => newThread()}>
            New conversation
          </button>
        </div>
        <p>
          Ask about your plans, availability, wishlist or saved coaching. Your
          verified identity and permitted records provide the context.
        </p>
        {context === "forever-behind" && (
          <p className="notice">
            Forever combat analysis is not available yet. Questions can use
            saved records and reviewed reports.
          </p>
        )}
        {!config?.jobsEnabled && (
          <p className="notice">
            New website questions are being prepared for activation. Saved
            answers remain available.
          </p>
        )}
        {error && (
          <p className="notice error" role="alert">
            {error}
          </p>
        )}
        {threads.length > 0 && (
          <label>
            Saved conversations
            <select
              value={
                threads.some(
                  (job) =>
                    job.command.type === "analysis" &&
                    job.command.conversationId === thread.id,
                )
                  ? thread.id
                  : ""
              }
              onChange={(event) => {
                const job = relevant.find(
                  (job) =>
                    job.command.type === "analysis" &&
                    job.command.conversationId === event.target.value,
                );
                if (job?.command.type === "analysis") {
                  setSaved([]);
                  setThread({
                    id: job.command.conversationId,
                    mode: job.command.mode,
                    characterId: job.command.characterId,
                    reportIds: job.command.reportIds,
                  });
                  setPrompt("");
                }
              }}
            >
              <option value="">New conversation</option>
              {threads.map(
                (job) =>
                  job.command.type === "analysis" && (
                    <option
                      key={job.command.conversationId}
                      value={job.command.conversationId}
                    >
                      {job.command.prompt.slice(0, 85)}
                    </option>
                  ),
              )}
            </select>
          </label>
        )}
        <div className="member-conversation" aria-live="polite">
          {turns.map(
            (job) =>
              job.command.type === "analysis" && (
                <article className="member-row" key={job.id}>
                  <p className="eyebrow">YOU ASKED</p>
                  <p className="preserve">{job.command.prompt}</p>
                  <p className="fine">
                    {new Date(job.createdAt).toLocaleString()} · {job.state}
                  </p>
                  {job.state === "complete" ? (
                    renderResult(job.result)
                  ) : (
                    <p role="status">
                      {job.state === "queued"
                        ? "Waiting for Guildmaster. Your request is saved."
                        : job.message}
                    </p>
                  )}
                  {["queued", "running", "paused"].includes(job.state) && (
                    <div className="form-actions">
                      <button
                        className="text-button"
                        onClick={() => void onChange(job.id, "cancel")}
                      >
                        Cancel request
                      </button>
                      {job.state === "paused" && (
                        <button
                          className="button"
                          onClick={() => void onChange(job.id, "resume")}
                        >
                          Resume request
                        </button>
                      )}
                    </div>
                  )}
                  {job.state === "failed" &&
                    Boolean(job.result) &&
                    renderResult(job.result)}
                </article>
              ),
          )}
        </div>
        <form
          className="member-form"
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            try {
              const ok = await onSubmit({
                type: "analysis",
                contextId: context,
                conversationId: thread.id,
                mode: thread.mode,
                prompt,
                ...(thread.characterId
                  ? { characterId: thread.characterId }
                  : {}),
                ...(thread.reportIds?.length
                  ? { reportIds: thread.reportIds }
                  : {}),
              });
              if (ok) setPrompt("");
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            {turns.length ? "Your follow-up" : "Your question"}
            <textarea
              required
              minLength={3}
              maxLength={12000}
              rows={5}
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="Am I rostered for the next raid?"
            />
          </label>
          {thread.characterId && (
            <p className="fine">
              This conversation uses your selected character and any report you
              chose. Ownership and report access are checked again for each
              request.
            </p>
          )}
          {context === "years-behind" && (
            <label>
              Request type
              <select
                value={thread.mode}
                onChange={(event) => newThread(event.target.value as Mode)}
              >
                <option value="explain">Explain saved information</option>
                <option value="player">Historical player analysis</option>
                {actor.officeAccess &&
                  actor.capabilities.includes("review") && (
                    <option value="guild">Private guild review</option>
                  )}
              </select>
            </label>
          )}
          <p className="fine">
            Asking does not change records, post to Discord or subscribe you to
            notifications.
          </p>
          <button
            className="button primary"
            disabled={
              busy || active || !config?.jobsEnabled || prompt.trim().length < 3
            }
          >
            {busy
              ? "Submitting…"
              : active
                ? "Waiting for current answer…"
                : turns.length
                  ? "Send follow-up"
                  : "Ask Guildmaster"}
          </button>
        </form>
      </section>
    </div>
  );
}
